import React from 'react';
import { Box, Button, Modal, Stack, Typography } from '@mui/material';
import MicOffIcon from '@mui/icons-material/MicOff';
import LaunchIcon from '@mui/icons-material/Launch';

export interface MicrophonePermissionModalProps {
  open: boolean;
  onClose: () => void;
  onRetry: () => void;
  /** Which browser the user is on — determines the instructions shown. */
  browserHint?: 'chrome' | 'edge' | 'firefox' | 'safari' | 'other';
}

function detectBrowser(): 'chrome' | 'edge' | 'firefox' | 'safari' | 'other' {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('firefox/')) return 'firefox';
  if (ua.includes('chrome/')) return 'chrome';
  if (ua.includes('safari/')) return 'safari';
  return 'other';
}

function getInstructions(
  browser: 'chrome' | 'edge' | 'firefox' | 'safari' | 'other',
): Array<{ step: string }> {
  switch (browser) {
    case 'chrome':
    case 'edge':
      return [
        { step: 'Click the tune/controls icon to the left of the URL bar.' },
        { step: 'Click "Site settings" (opens a new tab).' },
        { step: 'Find "Microphone" and set it to "Allow".' },
        { step: 'Return to this tab and click the mic icon again.' },
      ];
    case 'firefox':
      return [
        {
          step: "Voice dictation uses the Web Speech API, which Firefox doesn't support. Please use Chrome, Edge, or Safari.",
        },
      ];
    case 'safari':
      return [
        { step: 'Open Safari > Settings > Websites > Microphone.' },
        { step: 'Set this site to "Allow".' },
        { step: 'Reload the page and click the mic icon again.' },
      ];
    default:
      return [
        { step: "Open your browser's site settings for this page." },
        { step: 'Allow microphone access.' },
        { step: 'Reload and try again.' },
      ];
  }
}

export const MicrophonePermissionModal: React.FC<MicrophonePermissionModalProps> = ({
  open,
  onClose,
  onRetry,
  browserHint,
}) => {
  const browser = browserHint ?? detectBrowser();
  const instructions = getInstructions(browser);
  const canRetry = browser !== 'firefox';

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 480 },
          bgcolor: 'var(--bg)',
          borderRadius: 'var(--radius)',
          overflow: 'hidden',
          boxShadow:
            '0 25px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.05)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'var(--teal)',
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 'var(--radius-sm)',
              bgcolor: 'rgba(255,255,255,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MicOffIcon sx={{ color: 'var(--mint)', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1rem',
                color: 'white',
                lineHeight: 1.3,
              }}
            >
              Microphone access needed
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}
            >
              Voice dictation can't run without it
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: 'var(--t1)', mb: 2 }}>
            To use voice dictation, your browser needs permission to access your
            microphone. Follow these steps:
          </Typography>

          <Box
            component="ol"
            sx={{
              pl: 2.5,
              m: 0,
              mb: 2.5,
              '& li': {
                mb: 1,
                color: 'var(--t1)',
                fontSize: '0.875rem',
                lineHeight: 1.5,
              },
            }}
          >
            {instructions.map((item, i) => (
              <li key={i}>{item.step}</li>
            ))}
          </Box>

          {browser === 'chrome' || browser === 'edge' ? (
            <Box
              sx={{
                bgcolor: 'var(--s1)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                p: 1.5,
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <LaunchIcon sx={{ color: 'var(--t3)', fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: 'var(--t2)' }}>
                After changing the setting, come back here and click{' '}
                <strong>Try again</strong>.
              </Typography>
            </Box>
          ) : null}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              onClick={onClose}
              sx={{
                color: 'var(--t2)',
                fontWeight: 500,
                fontSize: '0.8rem',
                textTransform: 'none',
                px: 2,
                borderRadius: 'var(--radius-sm)',
              }}
            >
              Close
            </Button>
            {canRetry && (
              <Button
                variant="contained"
                onClick={onRetry}
                sx={{
                  bgcolor: 'var(--teal)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  px: 2.25,
                  borderRadius: 'var(--radius-sm)',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: 'var(--teal-mid)',
                    boxShadow: '0 2px 8px rgba(26,33,102,0.25)',
                  },
                }}
              >
                Try again
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default MicrophonePermissionModal;
